import BackendRepositoryRemote from '../api/BackendRepositoryRemote';
import { BackendRepository } from '../api/interfaces/BackendRepository';
import DateTime from '../helpers/DateTime';
import { AvailableNoSlots, DayPeriods, SaveNoSlotParams } from '../interfaces/AvailableNoSlots';
import { GroupedSlot, TopSlot } from '../interfaces/TopSlot';

export interface GetTopSlotsResult {
  requestedDateEnd: Date;
  requestsWithError: number;
  slots: GroupedSlot[];
}

export default class TopSlotService {
  private repository: BackendRepository;

  constructor() {
    this.repository = new BackendRepositoryRemote();
  }

  addWeekToDate(date: Date): void {
    const numberOfWeeks = 1;
    date.setDate(date.getDate() + (numberOfWeeks * 7));
  }

  public async bookSlot(ticketHashId: string, serviceAppointmentId: string, { start, end }: TopSlot): Promise<void> {
    const startISO = new Date(start).toISOString();
    const endISO = new Date(end).toISOString();

    return this.repository.bookSlot(ticketHashId, serviceAppointmentId, startISO, endISO);
  }

  public async getTopSlots(
    ticketHashId: string,
    serviceAppointmentId: string,
    earliestStartTime: Date,
    maxPlanDate: Date,
    abortSignal?: AbortSignal
  ): Promise<GetTopSlotsResult> {
    let retries = 0;
    let reachedTimeframeEnd = false;

    // create first week to request slots for
    const startDate: Date = DateTime.cloneDate(earliestStartTime);
    let endDate = DateTime.cloneDate(startDate);
    this.addWeekToDate(endDate);

    // shorten the week if it exceeds the limit
    if (endDate > maxPlanDate) {
      endDate = DateTime.cloneDate(maxPlanDate);
      reachedTimeframeEnd = true;
    }

    let slots: GroupedSlot[] = [];
    let requestsWithError = 0;

    while (!this.isMaxRetriesReached(retries) && this.isDateBeforeMaxPlanDate(maxPlanDate, endDate)) {
      if (retries > 0) {
        this.addWeekToDate(startDate);
        this.addWeekToDate(endDate);
      }

      if (endDate > maxPlanDate) {
        // we can exceed the timeframe limit when making retries
        endDate = DateTime.cloneDate(maxPlanDate);
        reachedTimeframeEnd = true;
      }

      const startDateISO = startDate.toISOString();
      const endDateISO = endDate.toISOString();

      try {
        slots = await this.repository.getTopSlots(ticketHashId, serviceAppointmentId, startDateISO, endDateISO, abortSignal);

        if (slots.length) {
          return {
            slots,
            requestedDateEnd: endDate,
            requestsWithError,
          };
        }
      } catch (err) {
        // we want to log the error except the ones from abort
        if (err && typeof err === 'object' && 'message' in err && err.message !== 'canceled') {
          console.log('getTopSlots error ', err);
        }

        if (abortSignal?.aborted) {
          // if abort signal was aborted, we don't need to continue retries
          break;
        }
        requestsWithError++;
      }

      if (reachedTimeframeEnd) {
        // if timeframe limit was reached, we don't need to continue retries
        break;
      }

      retries++;
    }

    return {
      slots,
      requestedDateEnd: endDate,
      requestsWithError,
    };
  }

  public saveNoSlotInformation(ticketHash: string, workorderId: string, params: SaveNoSlotParams): Promise<void> {
    return this.repository.saveNoSlotInformation(ticketHash, workorderId, params);
  }

  private isDateBeforeMaxPlanDate(maxPlanDate: Date, date?: Date): boolean {
    return (!date || date <= maxPlanDate);
  }

  private isMaxRetriesReached(retries: number): boolean {
    const maxRetries = 3;

    return retries >= (maxRetries + 1);
  }
}

export const getNumberOfSelectedDays = (slots: AvailableNoSlots): number => {
  return Object.values(slots).reduce((total, current: DayPeriods) => {
    return total + (current.afternoon ? 1 : 0) + (current.morning ? 1 : 0);
  }, 0);
};
